import {
  Modal,
  Button,
  Form,
  Input,
  Typography,
  Select,
  Tooltip,
  InputNumber,
  Checkbox,
  message,
} from 'antd';
import { format } from 'date-fns';
import { EditOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import {
  CreditCardRequest,
  CreditCardResponse2,
} from '../../model/Credit-card.model';
import CreditCardService from '../../service/credit-card-service/Credit-card.service';
import BankService from '../../service/bank-service/Bank.service';
import CardTypeService from '../../service/card-type-service/Card-type.service';

interface EditCreditCardModalProps {
  creditCard: CreditCardResponse2;
  customerId: number;
  onSuccess: () => void;
}

const { Text } = Typography;

const EditCreditCardModal: React.FC<EditCreditCardModalProps> = ({
  creditCard,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [banks, setBanks] = useState<any[]>([]);
  const [cardTypes, setCardTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreditCardRequest>({
    cardNumber: creditCard.cardNumber,
    cardTypeId: creditCard.cardType.id,
    bankId: creditCard.bank.id,
    expiryDate: creditCard.expiryDate,
    creditLimit: creditCard.creditLimit || undefined,
    notes: creditCard.notes || undefined,
    status: creditCard.status || undefined,
    defaultFeeRate: creditCard.defaultFeeRate || undefined,
    cyclePaymentDate: creditCard.cyclePaymentDate || undefined,
    customerId: creditCard.customer?.id,
    cardHolderName: creditCard.cardHolderName || null,
  });
  const [isSameAsCustomer, setIsSameAsCustomer] = useState(
    creditCard.cardHolderName === creditCard.customer?.name
  );
  const customerName = creditCard.customer?.name || '';

  // Hàm format số tiền sang dạng có dấu chấm
  const formatCurrency = (value: string) => {
    const number = value.replace(/\D/g, '');
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Validate default fee rate
  const validateDefaultFeeRate = (value: string) => {
    // Allow empty, numbers, and one decimal point with up to 2 decimal places
    return (
      /^$|^(\d+)?([.])?([0-9]{0,2})?$/.test(value) &&
      (value === '' || parseFloat(value) <= 100)
    );
  };

  // Validate cycle payment date (1-30)
  const validateCyclePaymentDate = (value: string) => {
    // Only allow whole numbers between 1-30
    const numValue = parseInt(value);
    return (
      value === '' || (!isNaN(numValue) && numValue >= 1 && numValue <= 30)
    );
  };

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        cardNumber: creditCard.cardNumber,
        cardTypeId: creditCard.cardType.id,
        bankId: creditCard.bank.id,
        expiryDate: creditCard.expiryDate,
        notes: creditCard.notes || '',
        creditLimit: creditCard.creditLimit || '',
        status: creditCard.status || undefined,
        defaultFeeRate: creditCard.defaultFeeRate || '',
        cyclePaymentDate: creditCard.cyclePaymentDate || '',
        cardHolderName: creditCard.cardHolderName || null,
      });

      // Load banks and card types
      const loadData = async () => {
        try {
          setLoading(true);
          const [banksData, cardTypesData] = await Promise.all([
            BankService.getBankList(),
            CardTypeService.getCardTypeList(),
          ]);
          setBanks(banksData);
          setCardTypes(cardTypesData);

          // Debug logs
          console.log('CreditCard data:', creditCard);
          console.log('CardTypes data:', cardTypesData);
          console.log('Banks data:', banksData);
        } catch (error) {
          console.error('Error loading banks and card types:', error);
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }
  }, [creditCard, open, form]);

  // Set form values when banks and cardTypes are loaded
  useEffect(() => {
    if (open && banks.length > 0 && cardTypes.length > 0) {
      form.setFieldsValue({
        cardNumber: creditCard.cardNumber,
        cardTypeId: creditCard.cardType.id,
        bankId: creditCard.bank.id,
        expiryDate: creditCard.expiryDate,
        notes: creditCard.notes || '',
        creditLimit: creditCard.creditLimit || '',
        status: creditCard.status || undefined,
        defaultFeeRate: creditCard.defaultFeeRate || '',
        cyclePaymentDate: creditCard.cyclePaymentDate || '',
        cardHolderName: creditCard.cardHolderName || null,
      });
    }
  }, [open, banks, cardTypes, creditCard, form]);

  // Update card holder name when checkbox changes
  useEffect(() => {
    if (isSameAsCustomer) {
      form.setFieldsValue({
        cardHolderName: customerName,
      });
    }
  }, [isSameAsCustomer, customerName, form]);

  // Handle checkbox change
  const handleCheckboxChange = (e: any) => {
    const checked = e.target.checked;
    setIsSameAsCustomer(checked);

    if (checked) {
      form.setFieldsValue({
        cardHolderName: customerName,
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const updatedData: CreditCardRequest = {
        ...formData,
        cardNumber: values.cardNumber,
        cardTypeId: values.cardTypeId,
        bankId: values.bankId,
        expiryDate: values.expiryDate,
        notes: values.notes,
        creditLimit: values.creditLimit,
        status: values.status,
        defaultFeeRate: values.defaultFeeRate,
        cyclePaymentDate: values.cyclePaymentDate,
        cardHolderName: values.cardHolderName,
        customerId: creditCard.customer?.id,
      };

      await CreditCardService.updateCreditCard(creditCard.id, updatedData);
      onSuccess();
      setOpen(false);
      message.success('Cập nhật thông tin thẻ thành công!');
    } catch (error: any) {
      console.error('Error updating credit card:', error);
      if (error.message?.includes('Số thẻ đã tồn tại')) {
        message.error('Số thẻ đã tồn tại trong hệ thống!');
      } else {
        message.error('Có lỗi khi cập nhật thông tin thẻ. Vui lòng thử lại!');
      }
    }
  };

  return (
    <>
      <Tooltip title="Chỉnh sửa">
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => setOpen(true)}
        />
      </Tooltip>

      <Modal
        title="Chỉnh sửa thông tin thẻ"
        open={open}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
        }}
        onOk={handleSubmit}
        okText="Hoàn thành"
        cancelText="Hủy"
        width={500}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            cardNumber: creditCard.cardNumber,
            cardTypeId: creditCard.cardType.id,
            bankId: creditCard.bank.id,
            expiryDate: creditCard.expiryDate,
            notes: creditCard.notes || '',
            creditLimit: creditCard.creditLimit || '',
            status: creditCard.status || undefined,
            defaultFeeRate: creditCard.defaultFeeRate || '',
            cyclePaymentDate: creditCard.cyclePaymentDate || '',
            cardHolderName: creditCard.cardHolderName || null,
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label={<Text strong>Số thẻ</Text>}
              name="cardNumber"
              rules={[{ required: true, message: 'Vui lòng nhập số thẻ!' }]}
            >
              <Input placeholder="Nhập số thẻ" />
            </Form.Item>

            <Form.Item
              label={<Text strong>Tên chủ thẻ</Text>}
              name="cardHolderName"
            >
              <div className="space-y-2">
                <Input
                  placeholder={
                    isSameAsCustomer
                      ? 'Sử dụng tên khách hàng'
                      : 'Nhập tên chủ thẻ'
                  }
                  disabled={isSameAsCustomer}
                />
                <Checkbox
                  checked={isSameAsCustomer}
                  onChange={handleCheckboxChange}
                >
                  Khách hàng ({customerName})
                </Checkbox>
              </div>
            </Form.Item>

            <Form.Item
              label={<Text strong>Ngân hàng</Text>}
              name="bankId"
              rules={[{ required: true, message: 'Vui lòng chọn ngân hàng!' }]}
            >
              <Select
                placeholder={`Chọn ngân hàng (hiện tại: ${creditCard.bank?.name || 'N/A'})`}
                loading={loading}
                showSearch
                notFoundContent={
                  loading ? 'Đang tải...' : 'Không tìm thấy ngân hàng'
                }
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {loading ? (
                  <Select.Option value="">Đang tải...</Select.Option>
                ) : (
                  banks.map((bank) => (
                    <Select.Option key={bank.id} value={bank.id}>
                      {bank.name}
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>

            <Form.Item
              label={<Text strong>Loại thẻ</Text>}
              name="cardTypeId"
              rules={[{ required: true, message: 'Vui lòng chọn loại thẻ!' }]}
            >
              <Select
                placeholder={`Chọn loại thẻ (hiện tại: ${creditCard.cardType?.name || 'N/A'})`}
                loading={loading}
                showSearch
                notFoundContent={
                  loading ? 'Đang tải...' : 'Không tìm thấy loại thẻ'
                }
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {loading ? (
                  <Select.Option value="">Đang tải...</Select.Option>
                ) : (
                  cardTypes.map((cardType) => (
                    <Select.Option
                      key={cardType.cardTypeId}
                      value={cardType.cardTypeId}
                    >
                      {cardType.name}
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>

            <Form.Item
              label={<Text strong>Ngày hết hạn</Text>}
              name="expiryDate"
            >
              <Input type="date" />
            </Form.Item>

            <Form.Item
              label={<Text strong>Hạn mức tín dụng</Text>}
              name="creditLimit"
            >
              <InputNumber
                style={{ width: '100%' }}
                formatter={(value) =>
                  value ? formatCurrency(value.toString()) : ''
                }
                parser={(value) => (value ? value.replace(/\./g, '') : '')}
                placeholder="Nhập hạn mức"
              />
            </Form.Item>

            <Form.Item label={<Text strong>Trạng thái</Text>} name="status">
              <Select placeholder="Chọn trạng thái">
                <Select.Option value="LUU_THE">Lưu thẻ</Select.Option>
                <Select.Option value="TRA_THE">Trả thẻ</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={<Text strong>Phí mặc định (%)</Text>}
              name="defaultFeeRate"
              help="Phí mặc định sẽ được áp dụng khi tạo lịch đáo hạn"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                precision={2}
                addonAfter="%"
                placeholder="Nhập phí mặc định (0-100%)"
              />
            </Form.Item>

            <Form.Item
              label={<Text strong>Ngày đến hạn hàng tháng</Text>}
              name="cyclePaymentDate"
              help="Ngày thanh toán của thẻ trong mỗi tháng (1-30)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={30}
                precision={0}
                placeholder="Nhập ngày đến hạn (1-30)"
              />
            </Form.Item>
          </div>

          <Form.Item label={<Text strong>Ghi chú</Text>} name="notes">
            <Input.TextArea placeholder="Thêm ghi chú" rows={3} />
          </Form.Item>

          {formData.expiryDate && (
            <div className="mb-4">
              <Text type="secondary">
                Ngày hết hạn:{' '}
                {format(new Date(formData.expiryDate), 'dd/MM/yyyy')}
              </Text>
            </div>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default EditCreditCardModal;
